import { tw } from "@careinfo/tailwind";
import { adToBs, bsToAd } from "@sbmdkl/nepali-date-converter";
import NepaliDate from "nepali-date-converter";
import React, { useEffect, useState } from "react";
import NepaliDatePickerWrapper from "./src/NepaliDatePicker";
type NpDatePickerProps = {
  label?: string;
  name: string;
  value: string;
  disabled?: boolean;
  setValue: (name: string, value: string, options?: any) => void;
  error?: any;
  disableFuture?: boolean;
};

const NpDatePicker: React.FC<NpDatePickerProps> = ({
  label,
  name,
  value,
  setValue,
  disabled = false,
  error: formError,
  disableFuture = false,
}) => {
  const [displayValue, setDisplayValue] = useState<string>(value);
  const [localError, setLocalError] = useState<string>("");
  const [calendarOpen, setCalendarOpen] = useState<boolean>(false);

  const isFutureDate = (bsDate: string): boolean => {
    try {
      const nepDate = new NepaliDate(bsDate);
      const adDate = nepDate.toJsDate();
      return adDate > new Date();
    } catch {
      return false;
    }
  };

  const handlePickerChange = (bsDate: string) => {
    setDisplayValue(bsDate);
    try {
      if (isFutureDate(bsDate)) {
        setLocalError("Future dates are not allowed");
        return;
      }
      if (bsDate !== displayValue) {
        const adDate = bsToAd(bsDate);
        setValue(name, adDate, { shouldValidate: true });
      }
      setLocalError("");
    } catch {
      setLocalError("Invalid BS date");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const typedDate = e.target.value;
    setDisplayValue(typedDate);

    const bsDateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!bsDateRegex.test(typedDate)) {
      setLocalError("Date must be in YYYY-MM-DD format");
      return;
    }

    try {
      if (isFutureDate(typedDate)) {
        setLocalError("Future dates are not allowed");
        return;
      }
      if (typedDate !== displayValue) {
        const adDate = bsToAd(typedDate);
        setValue(name, adDate, { shouldValidate: true });
      }
      setLocalError("");
    } catch {
      setLocalError("Invalid BS date");
    }
  };

  const toggleCalendar = () => {
    setCalendarOpen((prev) => !prev);
  };

  useEffect(() => {
    if (value) {
      try {
        const result = adToBs(value);
        let bsDate: string;
        if (typeof result === "string") {
          bsDate = result;
        }
        setDisplayValue(bsDate);
      } catch (err) {
        console.error("Invalid AD date:", value, err);
      }
    }
  }, [value]);

  const finalError = formError || localError;

  const isIncompleteDate =
    displayValue?.length > 0 && !/^\d{4}-\d{2}-\d{2}$/.test(displayValue);

  return (
    <div className={tw`flex flex-col gap-2 `}>
      {label && <label className={tw`font-medium text-sm`}>{label}</label>}

      <div className={tw`flex gap-2`}>
        <input
          type="text"
          disabled={disabled}
          placeholder="YYYY-MM-DD (BS)"
          value={displayValue}
          onChange={handleInputChange}
          className={tw`bg-[#EFF4F4] focus:outline-none py-2 px-4 w-full rounded-sm ${
            finalError ? "border border-red-500" : ""
          }`}
        />
        <button
          type="button"
          onClick={toggleCalendar}
          disabled={disabled || isIncompleteDate}
          className={tw`px-3 rounded-sm ${
            disabled || isIncompleteDate
              ? "bg-gray-200 cursor-not-allowed"
              : "bg-[#EFF4F4]"
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#000000"
            strokeLinecap="round"
            strokeLinejoin="round"
            height="20"
            width="20"
          >
            <path d="M8 2v4" strokeWidth="2"></path>
            <path d="M16 2v4" strokeWidth="2"></path>
            <path
              d="M5 4h14s2 0 2 2v14s0 2 -2 2H5s-2 0 -2 -2V6s0 -2 2 -2"
              strokeWidth="2"
            ></path>
            <path d="M3 10h18" strokeWidth="2"></path>
          </svg>
        </button>
      </div>

      {finalError && (
        <span className={tw`text-red-500 text-xs`}>{finalError}</span>
      )}

      <div className={tw`relative`}>
        <NepaliDatePickerWrapper
          inputClassName={tw`w-full hidden`}
          value={displayValue}
          onChange={handlePickerChange}
          options={{
            calenderLocale: "en",
            valueLocale: "en",
            closeOnSelect: true,
          }}
          isOpen={calendarOpen}
          disableFuture={disableFuture}
          setIsOpen={setCalendarOpen}
        />
      </div>
    </div>
  );
};

export default NpDatePicker;
